import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  ArrowForward,
  CheckCircle,
  ExpandMore,
  School,
  WorkOutline
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const recognitionItems = [
  'Je twijfelt of je huidige baan nog bij je past.',
  'Je wilt van richting veranderen, maar weet niet welke stap logisch is.',
  'Je hebt veel interesses en vindt het lastig om te kiezen.',
  'Je wilt met meer zelfvertrouwen solliciteren of onderhandelen.',
  'Je bent klaar voor groei, maar mist een concreet plan.'
];

const processSteps = [
  {
    number: '01',
    title: 'Gratis kennismaking',
    text: 'In een vrijblijvend online gesprek van 30 minuten bespreken we je vraag en voelen we of er een goede klik is.'
  },
  {
    number: '02',
    title: 'Inzicht en richting',
    text: 'We brengen je waarden, talenten, energiegevers en mogelijkheden in kaart. Zo ontstaat een koers die echt bij je past.'
  },
  {
    number: '03',
    title: 'Van inzicht naar actie',
    text: 'Je vertaalt je koers naar haalbare stappen. Met persoonlijke sessies, oefeningen en tussentijdse ondersteuning blijf je in beweging.'
  }
];

const faqs = [
  {
    question: 'Wat kost online loopbaancoaching?',
    answer:
      'Een losse coachsessie van één uur kost €80 inclusief btw. Het Platinum-traject kost €990 inclusief btw en bevat tien persoonlijke sessies, premium zelfcoachingsmateriaal, een persoonlijk ontwikkelingsplan en tussentijdse ondersteuning.'
  },
  {
    question: 'Wordt loopbaancoaching vergoed door mijn werkgever?',
    answer:
      'Veel werkgevers vergoeden loopbaancoaching geheel of gedeeltelijk vanuit een opleidingsbudget, persoonlijk ontwikkelbudget of budget voor duurzame inzetbaarheid. Vraag je leidinggevende of HR-afdeling welke regeling voor jou geldt.'
  },
  {
    question: 'Werkt online coaching net zo goed als coaching op locatie?',
    answer:
      'Ja. Online coaching biedt dezelfde persoonlijke aandacht en verdieping, zonder reistijd. Je spreekt Hanneke via videobellen vanuit een plek waar jij je prettig voelt en ontvangt oefeningen digitaal.'
  },
  {
    question: 'Hoe lang duurt een loopbaancoachingstraject?',
    answer:
      'Dat hangt af van je vraag. Eén sessie kan genoeg zijn voor een concrete keuze of voorbereiding. Voor duurzame loopbaanontwikkeling bestaat het Platinum-traject uit tien sessies, gepland in een tempo dat bij jou past.'
  },
  {
    question: 'Voor wie is deze loopbaancoaching geschikt?',
    answer:
      'De coaching is er vooral voor studenten, starters en young professionals die richting zoeken, willen doorgroeien, een carrièreswitch overwegen of met meer vertrouwen willen solliciteren.'
  }
];

const Loopbaancoaching = () => {
  return (
    <Box component="main">

      <Box
        component="section"
        sx={{
          bgcolor: 'primary.dark',
          color: 'primary.contrastText',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            width: 360,
            height: 360,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.06)',
            right: -100,
            top: -160
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Chip
            label="Voor studenten en young professionals"
            sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.14)', color: 'white' }}
          />
          <Typography
            component="h1"
            variant="h1"
            sx={{ fontSize: { xs: '2.45rem', md: '4rem' }, lineHeight: 1.08, mb: 3 }}
          >
            Online loopbaancoaching voor een carrière die bij je past
          </Typography>
          <Typography
            component="p"
            sx={{ fontFamily: '"Poppins", sans-serif', fontSize: { xs: '1.05rem', md: '1.25rem' }, lineHeight: 1.75, maxWidth: 760, mx: 'auto', mb: 4 }}
          >
            Krijg helderheid over wat je wilt, ontdek waar je kracht ligt en zet concrete
            stappen naar werk dat energie geeft — met persoonlijke begeleiding van
            loopbaancoach Hanneke van Keulen.
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            state={{ subject: 'Gratis kennismakingsgesprek loopbaancoaching' }}
            variant="contained"
            color="secondary"
            size="large"
            endIcon={<ArrowForward />}
          >
            Plan je gratis kennismakingsgesprek
          </Button>
          <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.82)' }}>
            Vrijblijvend, online en 30 minuten
          </Typography>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography component="h2" variant="h2" gutterBottom>
                Herken je dit?
              </Typography>
              <Typography color="text.secondary">
                Je hoeft niet vast te lopen om hulp te vragen. Vaak voel je al een tijd dat
                er iets mag veranderen, maar is het lastig om alleen tot de kern te komen.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: { xs: 2.5, md: 4 } }}>
                <List disablePadding>
                  {recognitionItems.map((item) => (
                    <ListItem key={item} disableGutters>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircle color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ fontFamily: '"Poppins", sans-serif' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'rgba(139,125,107,0.08)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: 6 }}>
            <Typography component="h2" variant="h2" gutterBottom>
              Zo werkt online loopbaancoaching
            </Typography>
            <Typography color="text.secondary">
              Geen standaardadvies, maar een persoonlijk traject dat past bij jouw vraag,
              tempo en ambities.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {processSteps.map((step) => (
              <Grid item xs={12} md={4} key={step.number}>
                <Card sx={{ height: '100%', mb: 0 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      aria-hidden="true"
                      sx={{ color: 'secondary.main', fontSize: '2rem', fontWeight: 700, mb: 2 }}
                    >
                      {step.number}
                    </Typography>
                    <Typography component="h3" variant="h4" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography color="text.secondary">{step.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/contact"
              state={{ subject: 'Gratis kennismakingsgesprek loopbaancoaching' }}
              variant="contained"
              size="large"
            >
              Plan je gratis kennismakingsgesprek
            </Button>
          </Box>
        </Container>
      </Box>

      <Box component="section" id="tarieven" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: 5 }}>
            <Typography component="h2" variant="h2" gutterBottom>
              Tarieven loopbaancoaching
            </Typography>
            <Typography color="text.secondary">
              Je weet vooraf waar je aan toe bent. Alle prijzen zijn inclusief btw en er zijn
              geen verborgen kosten.
            </Typography>
          </Box>
          <TableContainer component={Paper}>
            <Table aria-label="Tarieven online loopbaancoaching">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.dark' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Vorm</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Dit krijg je</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>
                    Tarief
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
                    <Typography component="h3" variant="h6">
                      Losse sessie
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Flexibel en doelgericht
                    </Typography>
                  </TableCell>
                  <TableCell>
                    Coachsessie van één uur, voorbereiding en follow-up, toegang tot het
                    Essential Zelfcoaching Pakket, hand-outs en beperkt tussentijds
                    e-mailcontact.
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                    €80 / uur
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography component="h3" variant="h6">
                      Platinum
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Volledig traject
                    </Typography>
                  </TableCell>
                  <TableCell>
                    Alles uit Elite zelfcoaching, 10 persoonlijke coachsessies van één uur,
                    een persoonlijk ontwikkelingsplan, directe toegang tot alle premium
                    content en WhatsApp-ondersteuning tussen de sessies.
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                    €990
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 4 }}
          >
            <Button component={RouterLink} to="/contact" variant="contained">
              Bespreek welk traject bij je past
            </Button>
            <Link component={RouterLink} to="/sollicitatietraining" underline="hover">
              Bekijk ook sollicitatietraining
            </Link>
          </Stack>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 9 }, bgcolor: 'primary.dark', color: 'white' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <WorkOutline sx={{ fontSize: 44, mb: 2, color: 'secondary.light' }} />
          <Typography component="h2" variant="h2" gutterBottom>
            Mogelijk vergoed door je werkgever
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.86)', mb: 3 }}>
            Veel werkgevers betalen loopbaancoaching vanuit een opleidingsbudget, persoonlijk
            ontwikkelbudget of budget voor duurzame inzetbaarheid. Vraag HR of je leidinggevende
            welke mogelijkheden er zijn en leg uit hoe het traject bijdraagt aan je ontwikkeling.
          </Typography>
          <Link
            component={RouterLink}
            to="/tips#loopbaancoaching-vergoed-werkgever"
            sx={{ color: 'secondary.light', fontWeight: 700 }}
          >
            Binnenkort: zo vraag je vergoeding aan bij je werkgever
          </Link>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src="/Photo1_me.png"
                alt="Loopbaancoach Hanneke van Keulen"
                width="858"
                height="1150"
                loading="lazy"
                sx={{ display: 'block', width: '100%', maxHeight: 560, objectFit: 'cover', objectPosition: 'center 20%', borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Chip icon={<School />} label="Loopbaancoach & docent" sx={{ mb: 2 }} />
              <Typography component="h2" variant="h2" gutterBottom>
                Over Hanneke van Keulen
              </Typography>
              <Typography paragraph color="text.secondary">
                Ik help studenten en young professionals om helderheid te krijgen over wie ze
                zijn, waar hun kracht ligt en welke loopbaan daarbij past. Mijn aanpak is warm,
                doelgericht en praktisch: ruimte voor echte reflectie, altijd met een volgende
                stap in beeld.
              </Typography>
              <Typography paragraph color="text.secondary">
                Naast mijn coachingspraktijk geef ik onderwijs en trainingen op het gebied van
                loopbaanontwikkeling. Ik heb onder meer doceerervaring bij Wageningen University
                &amp; Research (WUR) en de Universiteit Utrecht (UU). Daardoor combineer ik
                persoonlijke aandacht met wetenschappelijk onderbouwde inzichten.
              </Typography>
              <Button component={RouterLink} to="/over" variant="outlined" endIcon={<ArrowForward />}>
                Lees meer over Hanneke
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: 'rgba(139,125,107,0.08)' }}>
        <Container maxWidth="lg">
          <Typography component="h2" variant="h2" align="center" sx={{ mb: 5 }}>
            Ervaringen met Hanneke
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography sx={{ fontStyle: 'italic', mb: 3 }}>
                  “Met Hannekes hulp ben ik niet langer bang voor mijn carrière na mijn
                  afstuderen. Ze hielp me meer vertrouwen te krijgen in mezelf en mijn toekomst.”
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar>YZ</Avatar>
                  <Box>
                    <Typography fontWeight={700}>Yueran Z.</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Masterstudent WUR
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography sx={{ fontStyle: 'italic', mb: 3 }}>
                  “Hannekes enthousiasme en energie maakten het een zeer prettige en goede
                  cursus. Het was geweldig om haar als mentor te hebben.”
                </Typography>
                {/* TODO HANNEKE: vervang deze anonieme site-testimonial door een echte, benoemde testimonial met toestemming. */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar>TC</Avatar>
                  <Box>
                    <Typography fontWeight={700}>Tevreden cliënt</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cursist
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="md">
          <Typography component="h2" variant="h2" align="center" sx={{ mb: 5 }}>
            Veelgestelde vragen over loopbaancoaching
          </Typography>
          {faqs.map((faq) => (
            <Accordion key={faq.question} disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography component="h3" sx={{ fontWeight: 700 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
          <Typography align="center" sx={{ mt: 4 }}>
            Meer praktische inspiratie vind je bij de{' '}
            <Link component={RouterLink} to="/tips">
              loopbaan- en sollicitatietips
            </Link>
            .
          </Typography>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, bgcolor: 'secondary.light' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography component="h2" variant="h2" gutterBottom>
            Klaar om helderheid te krijgen over je volgende stap?
          </Typography>
          <Typography sx={{ mb: 4, maxWidth: 650, mx: 'auto' }}>
            In een gratis online kennismaking bespreken we waar je nu staat, wat je nodig hebt
            en hoe coaching je verder kan helpen. Zonder verplichtingen, met alle ruimte voor
            jouw vragen.
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            state={{ subject: 'Gratis kennismakingsgesprek loopbaancoaching' }}
            variant="contained"
            color="primary"
            size="large"
            endIcon={<ArrowForward />}
          >
            Plan je gratis kennismakingsgesprek
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Loopbaancoaching;
